<template>
    <div>
        <!-- Bouton IA -->
        <div class="btnAI" :class="{ 'btnAI--spot': coachVisible }" @click="openChat">
            <img src="/media/bo/icons/ai/iconHasbg.png" alt="ai-icon" height="12" />
            {{ $t('chatbot.button_label') }}
            <!-- Clé manquante : Louise a un message. C'est le seul moment où la pastille se lit. -->
            <span v-if="keyMissing" class="btnAI__badge" :aria-label="$t('chatbot.gemini_key_badge')">1</span>
            <span v-if="coachVisible" class="btnAI__halo" aria-hidden="true"></span>
        </div>

        <!-- Première visite : le panneau reste FERMÉ, on désigne le bouton. -->
        <div v-if="coachVisible" class="coach-veil" @click="dismissCoach"></div>
        <div v-if="coachVisible" class="coach" role="dialog" :aria-label="$t('chatbot.coach_title')">
            <div class="coach__spark"><Sparkles :size="20" /></div>
            <h3>{{ $t('chatbot.coach_title') }}</h3>
            <p>{{ $t('chatbot.coach_body') }}</p>
            <ul>
                <li v-for="(item, iIndex) in coachItems" :key="iIndex">
                    <Check :size="17" />
                    <span>{{ item }}</span>
                </li>
            </ul>
            <div class="coach__actions">
                <button class="coach__go" type="button" @click="openFromCoach">
                    {{ $t('chatbot.coach_cta') }}
                </button>
                <button class="coach__later" type="button" @click="dismissCoach">
                    {{ $t('chatbot.coach_later') }}
                </button>
            </div>
        </div>

        <!-- Fenêtre de chat -->
        <!-- Toujours monté : à la fermeture le panneau se replie VERS le bouton qui l'a ouvert
             (transform-origin en haut à droite) au lieu de disparaître d'un coup. -->
        <div class="chat-popup" :class="{ 'chat-popup--closed': !chatVisible }">
            <div class="chat-header">
                <div class="header-identity">
                    <div class="header-avatar" aria-hidden="true">
                        <img v-if="isDark" src="/media/bo/icons/ai/iconNoBg.png" alt="" />
                        <img v-if="!isDark" src="/media/bo/icons/ai/iconLight.png" alt="" />
                        <span class="header-status-dot" :class="{ 'header-status-dot--off': keyMissing }"></span>
                    </div>
                    <div class="header-text ppv2-text-black">
                        <span class="header-title">{{ $t('chatbot.header_title') }}</span>
                        <span class="header-subtitle" :class="{ 'header-subtitle--off': keyMissing }">
                            {{ keyMissing ? $t('chatbot.status_offline') : $t('chatbot.status_online') }}
                        </span>
                    </div>
                </div>
                <button class="close-btn ppv2-text-black" :aria-label="$t('chatbot.close')" @click="closeChat"><X /></button>
            </div>

            <div class="chat-body" ref="chatBody">
                <!-- Aucun message : salutation + actions rapides, ancrées en bas (près du champ,
                     là où le regard arrive) plutôt que flottantes en haut du vide. -->
                <div v-if="!hasMessages" class="chat-intro ppv2-text-black">
                    <div class="greet">
                        <h3 class="ppv2-text-black">{{ greeting }}</h3>
                        <p>{{ $t('chatbot.greet_question') }}</p>
                    </div>
                    <div class="sugg-zone">
                        <div class="sugg">
                            <button
                                v-for="(action, aIndex) in quickActions"
                                :key="action.key"
                                class="sugg-card"
                                type="button"
                                :style="{ animationDelay: (0.04 + aIndex * 0.045).toFixed(3) + 's' }"
                                @click="sendSuggestion(action.label)"
                            >
                                <span class="sugg-ico" :class="action.tone">
                                    <component :is="action.icon" :size="16" />
                                </span>
                                <b>{{ action.label }}</b>
                                <span class="go"><ArrowRight :size="17" /></span>
                            </button>
                        </div>
                        <!-- Questions par défaut, conservées sous les actions. -->
                        <!--div class="sugg-questions">
                            <button
                                v-for="(question, qIndex) in defaultQuestions"
                                :key="qIndex"
                                class="sugg-question"
                                type="button"
                                @click="sendSuggestion(question)"
                            >
                                {{ question }}
                            </button>
                        </div-->
                    </div>
                </div>

                <!-- Messages -->
                <div v-else>
                    <div v-for="(msg, index) in messages" :key="index">
                        <div
                            :class="[
                                'message',
                                msg.sender === 'user' ? 'user-message ppv2-text-black' : 'ai-message ppv2-text-black',
                            ]"
                            v-html="msg.text"
                        ></div>
                        <!-- Clé Gemini manquante : saisie directe, sans quitter le chat.
                             L'enregistrement passe par l'endpoint Intégrations, qui porte la permission. -->
                        <div v-if="msg.keyForm" class="key-card">
                            <p v-if="msg.keySaved" class="key-card__done">
                                <Check :size="15" /> {{ $t('chatbot.gemini_key_saved_inline') }}
                            </p>
                            <template v-else>
                                <!-- Lien en dur : formatMarkdown neutralise les liens externes
                                     (ils viennent du modèle), celui-ci est écrit par nous. -->
                                <p class="key-card__hint">
                                    {{ $t('chatbot.gemini_key_hint_before') }}
                                    <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener"
                                        >Google AI Studio</a
                                    >{{ $t('chatbot.gemini_key_hint_after') }}
                                </p>
                                <div class="key-card__row">
                                    <input
                                        type="password"
                                        v-model="geminiKey"
                                        :placeholder="geminiKeyPlaceholder"
                                        class="key-card__input"
                                        autocomplete="off"
                                        spellcheck="false"
                                        :disabled="keyPending"
                                        @input="keyError = ''"
                                        @keyup.enter="saveGeminiKey(msg)"
                                    />
                                    <button
                                        class="key-card__btn"
                                        type="button"
                                        :disabled="keyPending"
                                        @click="saveGeminiKey(msg)"
                                    >
                                        {{ keyPending ? $t('chatbot.gemini_key_saving') : $t('chatbot.gemini_key_activate') }}
                                    </button>
                                </div>
                                <p v-if="keyError" class="key-card__err">{{ keyError }}</p>
                            </template>
                        </div>
                        <div v-if="msg.actions && msg.actions.length" class="message-actions">
                            <button
                                v-for="(action, aIndex) in msg.actions"
                                :key="aIndex"
                                :class="[
                                    'action-btn',
                                    action.type === 'confirm' ? 'action-btn--confirm' : 'action-btn--navigate',
                                    { 'action-btn--done': action.done },
                                ]"
                                :disabled="action.done || actionPending"
                                type="button"
                                @click="runAction(action)"
                            >
                                <span v-if="action.type === 'navigate'" class="action-btn__icon" aria-hidden="true">→</span>
                                <span v-if="action.done" class="action-btn__icon" aria-hidden="true">✓</span>
                                {{ action.label }}
                            </button>
                        </div>
                    </div>
                    <!-- Indicateur de frappe -->
                    <div
                        v-if="isLoading"
                        class="message ai-message typing-indicator"
                        aria-live="polite"
                        :aria-label="$t('chatbot.header_title')"
                    >
                        <span class="typing-dot"></span>
                        <span class="typing-dot"></span>
                        <span class="typing-dot"></span>
                    </div>
                </div>
            </div>

            <div class="chat-footer">
                <div class="border-footer">
                    <input
                        type="text"
                        :placeholder="$t('chatbot.input_placeholder')"
                        v-model="messageText"
                        :disabled="isLoading"
                        @keyup.enter="sendMessage"
                        class="ppv2-text-black"
                        ref="messageInput"
                        :style="{ fontSize: normalizeFontSize(14), padding: '10px ' + normalizeSize(16, true) }"
                    />
                    <button
                        class="send-btn"
                        :disabled="isLoading || !messageText.trim()"
                        @click="sendMessage"
                        :class="{ 'ppv2-text-[#7D2DBD]': !isDark, 'ppv2-text-white/50': isDark }"
                    >
                        <SendHorizontal />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    X,
    SendHorizontal,
    Sparkles,
    Calendar,
    Search,
    BarChart3,
    FileText,
    Plus,
    TrendingUp,
    ArrowRight,
    Check,
} from 'lucide-vue'
import { EventBus } from '../../../../eventBus.js'
import normalizeSize from '../../../../utils/normalizeSize.js'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

/**
 * Le widget est monté DEUX fois (navbar desktop + navbar mobile) : un seul des deux boutons est
 * visible selon la largeur. Sans ce verrou, la bulle de découverte s'afficherait en double —
 * elle est en position:fixed, donc l'instance au bouton masqué en dessinerait une deuxième,
 * pointant vers rien.
 */
let coachClaimed = false

export default {
    name: 'ChatAssistant',
    components: { X, SendHorizontal, Sparkles, ArrowRight, Check },
    data() {
        return {
            chatVisible: false,
            messageText: '',
            hasMessages: false,
            messages: [],
            conversationId: null,
            csrfToken: '',
            historyLoaded: false,
            isLoading: false,
            actionPending: false,
            geminiKey: '',
            keyPending: false,
            keyError: '',
            lastUserMessage: '',
            keyMissing: !!(window.AiAssistant && window.AiAssistant.key_missing),
            coachVisible: false,
            coachTimer: null,
            // Le placeholder reprend le format réel d'une clé Gemini (AIza + 35 caractères) :
            // on reconnaît ce qu'on doit coller, et une clé d'un autre service se repère avant de valider.
            geminiKeyPlaceholder: 'AIzaSyB4x7Qk2mN9pR3tV6wZ1aC8eG5hJ0lM7nP',
        }
    },
    computed: {
        /** Salutation selon l'heure + prénom (window.User est le pont d'identité du BO). */
        greeting() {
            const name = (window.User && window.User.name ? String(window.User.name) : '').trim().split(' ')[0]
            const hello = new Date().getHours() >= 18 ? this.$t('chatbot.good_evening') : this.$t('chatbot.good_morning')
            return name ? `${hello} ${name},` : `${hello},`
        },
        /**
         * Sept actions courtes plutôt que trois questions longues : l'icône encode la famille
         * (violet = consulter, bleu = analyser, vert = créer).
         */
        quickActions() {
            return [
                { key: 'day', icon: Sparkles, tone: 'i-vio', label: this.$t('chatbot.action_day') },
                { key: 'calendar', icon: Calendar, tone: 'i-vio', label: this.$t('chatbot.action_calendar') },
                { key: 'search', icon: Search, tone: 'i-vio', label: this.$t('chatbot.action_search') },
                { key: 'stats', icon: BarChart3, tone: 'i-blu', label: this.$t('chatbot.action_stats') },
                { key: 'devis', icon: FileText, tone: 'i-gre', label: this.$t('chatbot.action_devis') },
                { key: 'booking', icon: Plus, tone: 'i-gre', label: this.$t('chatbot.action_booking') },
                { key: 'perf', icon: TrendingUp, tone: 'i-blu', label: this.$t('chatbot.action_performance') },
            ]
        },
        coachItems() {
            return [this.$t('chatbot.coach_item_1'), this.$t('chatbot.coach_item_2'), this.$t('chatbot.coach_item_3')]
        },
        defaultQuestions() {
            return [
                this.$t('chatbot.suggestion_reservations'),
                this.$t('chatbot.suggestion_turnover'),
                this.$t('chatbot.suggestion_invoices'),
            ]
        },
        isDark() {
            return this.theme.mode === 'dark'
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        openChat() {
            this.chatVisible = !this.chatVisible
            if (!this.chatVisible) return

            this.dismissCoach()
            this.loadHistory()

            // Clé manquante : à la 1re ouverture on montre l'outil (les suggestions) avant de
            // demander quoi que ce soit ; aux ouvertures suivantes la pastille annonçait un
            // message, on ouvre donc directement dessus sans faire taper une question pour rien.
            if (this.keyMissing && this.readFlag('panel_seen') && !this.hasMessages) {
                this.showKeyRequest()
            }
            this.writeFlag('panel_seen')

            this.$nextTick(() => {
                if (this.$refs.messageInput) this.$refs.messageInput.focus()
            })
        },
        closeChat() {
            this.chatVisible = false
        },
        /** Message de demande de clé, sans bulle utilisateur (pas de question posée). */
        showKeyRequest() {
            this.displayMessage(this.$t('chatbot.gemini_key_request'), 'ai', [], true)
            this.hasMessages = true
        },
        openFromCoach() {
            this.dismissCoach()
            if (!this.chatVisible) this.openChat()
        },
        dismissCoach() {
            if (!this.coachVisible) return
            this.coachVisible = false
            // Drapeau porté par l'établissement : une fois vue, l'équipe entière ne la revoit plus.
            if (window.AiAssistant) window.AiAssistant.coach_seen = true
            if (window.axios) {
                window.axios.post('/back-office/ai/coach/seen').catch((e) => {
                    console.error('[AI Chat] Coach flag error:', e)
                })
            }
        },
        onEscape(e) {
            if (e.key !== 'Escape') return
            if (this.coachVisible) this.dismissCoach()
            else if (this.chatVisible) this.closeChat()
        },
        /**
         * Drapeaux d'affichage, par utilisateur, dans le navigateur.
         * ponytail: localStorage plutôt qu'une migration sur la table users de CHAQUE tenant pour
         * un drapeau d'onboarding — le coach peut réapparaître une fois sur un autre poste.
         */
        flagKey(name) {
            const userId = window.User && window.User.id ? window.User.id : 'anon'
            return `pp_ai_${name}_${userId}`
        },
        readFlag(name) {
            try {
                return window.localStorage.getItem(this.flagKey(name)) === '1'
            } catch (e) {
                return false
            }
        },
        writeFlag(name) {
            try {
                window.localStorage.setItem(this.flagKey(name), '1')
            } catch (e) {
                /* navigation privée / stockage bloqué : le coach se remontrera, sans casser la page */
            }
        },
        async loadHistory() {
            if (this.historyLoaded) return
            try {
                const http = window.axios
                if (!http) return
                const response = await http.get('/back-office/ai/history', {
                    params: { conversation_id: this.conversationId },
                })
                const data = response.data || {}
                if (Array.isArray(data.messages) && data.messages.length > 0) {
                    this.messages = data.messages.map((msg) => ({
                        text: msg.role === 'assistant' ? this.formatMarkdown(msg.content) : this.escapeHTML(msg.content),
                        sender: msg.role === 'assistant' ? 'ai' : 'user',
                    }))
                    this.hasMessages = true
                }
                if (data.conversation_id) {
                    this.conversationId = data.conversation_id
                }
                this.historyLoaded = true
                this.scrollToBottom()
            } catch (e) {
                console.error('[AI Chat] History load error:', e)
            }
        },
        sendSuggestion(text) {
            this.messageText = text
            this.sendMessage()
        },
        async sendMessage() {
            const text = this.messageText.trim()
            if (!text || this.isLoading) return

            this.displayMessage(text, 'user')
            this.lastUserMessage = text
            this.messageText = ''
            this.hasMessages = true
            this.isLoading = true
            this.scrollToBottom()

            try {
                const http = window.axios
                const response = await http.post('/back-office/ai/chat', {
                    message: text,
                    conversation_id: this.conversationId,
                })
                const data = response.data
                if (data.conversation_id) {
                    this.conversationId = data.conversation_id
                }
                if (data.response) {
                    this.displayMessage(data.response, 'ai', data.actions, data.needs_gemini_key)
                } else {
                    this.displayMessage(this.$t('chatbot.error_no_response'), 'ai')
                }
            } catch (e) {
                console.error(e)
                this.displayMessage(this.$t('chatbot.error_connection'), 'ai')
            } finally {
                this.isLoading = false
            }

            this.scrollToBottom()
            this.$nextTick(() => {
                if (this.$refs.messageInput) this.$refs.messageInput.focus()
            })
        },
        displayMessage(text, sender, actions, keyForm) {
            const formatted = sender === 'ai' ? this.formatMarkdown(text) : this.escapeHTML(text)
            const safeActions = Array.isArray(actions)
                ? actions
                      .filter(
                          (a) =>
                              a &&
                              a.label &&
                              ((a.type === 'navigate' && typeof a.url === 'string') ||
                                  (a.type === 'confirm' && typeof a.token === 'string'))
                      )
                      .map((a) => ({ ...a, done: false }))
                : []
            this.messages.push({ text: formatted, sender, actions: safeActions, keyForm: !!keyForm })
        },

        /**
         * Enregistre la clé Gemini sans quitter le chat.
         * L'écriture passe par l'endpoint Intégrations existant : c'est lui qui chiffre le secret
         * (SecretService) et qui porte la permission « Modifier une intégration » — le widget ne
         * décide rien, un utilisateur sans droit reçoit un 403 du serveur.
         */
        async saveGeminiKey(msg) {
            const key = this.geminiKey.trim()
            if (this.keyPending) return
            if (!key) {
                this.keyError = this.$t('chatbot.gemini_key_empty')
                return
            }

            this.keyPending = true
            this.keyError = ''
            try {
                await window.axios.post('/back-office/v3/integration/update-settings', {
                    id: 'gemini',
                    settings: [{ key: 'gemini_api_key', value: key }],
                })
                this.geminiKey = ''
                this.keyMissing = false
                if (window.AiAssistant) window.AiAssistant.key_missing = false
                this.$set(msg, 'keySaved', true)

                // On relance la question restée sans réponse ; sinon on revient aux suggestions.
                if (this.lastUserMessage) {
                    this.messageText = this.lastUserMessage
                    await this.sendMessage()
                }
            } catch (e) {
                const status = e && e.response ? e.response.status : 0
                this.keyError = status === 403 ? this.$t('chatbot.gemini_key_forbidden') : this.$t('chatbot.gemini_key_error')
            } finally {
                this.keyPending = false
                this.scrollToBottom()
            }
        },
        async runAction(action) {
            if (action.done || this.actionPending) return

            if (action.type === 'navigate') {
                if (action.url && action.url.indexOf(window.location.origin) === 0) {
                    window.location.href = action.url
                }
                return
            }

            if (action.type === 'confirm') {
                this.actionPending = true
                try {
                    const response = await window.axios.post('/back-office/ai/action/confirm', {
                        token: action.token,
                        conversation_id: this.conversationId,
                    })
                    const data = response.data || {}
                    action.done = true
                    this.displayMessage((data.success ? '✓ ' : '') + (data.message || this.$t('chatbot.error_no_response')), 'ai')
                } catch (e) {
                    console.error('[AI Chat] Action confirm error:', e)
                    this.displayMessage(this.$t('chatbot.error_connection'), 'ai')
                } finally {
                    this.actionPending = false
                    this.scrollToBottom()
                }
            }
        },
        escapeHTML(text) {
            const div = document.createElement('div')
            div.textContent = text
            return div.innerHTML
        },
        formatMarkdown(text) {
            if (!text) return ''
            // 1. Escape HTML first (XSS safety)
            let html = this.escapeHTML(text)

            // 2. Parse block-level structures line-by-line
            const lines = html.split('\n')
            const blocks = []
            let i = 0

            while (i < lines.length) {
                const line = lines[i]

                // Markdown table: header | header \n --- | --- \n row | row
                if (
                    i + 1 < lines.length &&
                    /^\s*\|.+\|\s*$/.test(line) &&
                    /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(lines[i + 1])
                ) {
                    const headerCells = line
                        .trim()
                        .replace(/^\||\|$/g, '')
                        .split('|')
                        .map((c) => c.trim())
                    const rows = []
                    let j = i + 2
                    while (j < lines.length && /^\s*\|.+\|\s*$/.test(lines[j])) {
                        rows.push(
                            lines[j]
                                .trim()
                                .replace(/^\||\|$/g, '')
                                .split('|')
                                .map((c) => c.trim())
                        )
                        j++
                    }
                    const thead = '<thead><tr>' + headerCells.map((c) => `<th>${c}</th>`).join('') + '</tr></thead>'
                    // data-label : sous 3 colonnes visibles la popup est trop étroite, le CSS empile
                    // alors chaque ligne en « libellé : valeur » plutôt que d'imposer un scroll latéral.
                    const tbody =
                        '<tbody>' +
                        rows
                            .map(
                                (r) =>
                                    '<tr>' +
                                    r.map((c, ci) => `<td data-label="${headerCells[ci] || ''}">${c}</td>`).join('') +
                                    '</tr>'
                            )
                            .join('') +
                        '</tbody>'
                    // Au-delà de 3 colonnes, la popup (400px) ne peut pas afficher le tableau sans
                    // couper : on empile chaque ligne en « libellé : valeur » (voir .md-table--stacked)
                    // plutôt que d'imposer un scroll latéral à l'utilisateur.
                    const tableClass = headerCells.length > 3 ? 'md-table md-table--stacked' : 'md-table'
                    blocks.push(`<div class="md-table-wrap"><table class="${tableClass}">${thead}${tbody}</table></div>`)
                    i = j
                    continue
                }

                // Citation : > texte
                if (/^\s*(&gt;|>)\s?/.test(line)) {
                    const quoted = []
                    while (i < lines.length && /^\s*(&gt;|>)\s?/.test(lines[i])) {
                        quoted.push(lines[i].replace(/^\s*(&gt;|>)\s?/, ''))
                        i++
                    }
                    blocks.push(`<blockquote class="md-quote">${quoted.join('<br>')}</blockquote>`)
                    continue
                }

                // Headers: ### Title
                const headerMatch = line.match(/^(#{1,3})\s+(.*)$/)
                if (headerMatch) {
                    const level = headerMatch[1].length + 2 // #->h3, ##->h4, ###->h5
                    blocks.push(`<h${level} class="md-h">${headerMatch[2]}</h${level}>`)
                    i++
                    continue
                }

                // Unordered list
                if (/^\s*[-*]\s+/.test(line)) {
                    const items = []
                    while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
                        items.push(lines[i].replace(/^\s*[-*]\s+/, ''))
                        i++
                    }
                    blocks.push('<ul class="md-ul">' + items.map((it) => `<li>${it}</li>`).join('') + '</ul>')
                    continue
                }

                // Ordered list
                if (/^\s*\d+\.\s+/.test(line)) {
                    const items = []
                    while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
                        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''))
                        i++
                    }
                    blocks.push('<ol class="md-ol">' + items.map((it) => `<li>${it}</li>`).join('') + '</ol>')
                    continue
                }

                // Empty line → paragraph break
                if (line.trim() === '') {
                    blocks.push('')
                    i++
                    continue
                }

                // Plain line (accumulate consecutive lines into a paragraph)
                const paragraph = [line]
                i++
                while (
                    i < lines.length &&
                    lines[i].trim() !== '' &&
                    !/^(#{1,3}\s|[-*]\s|\d+\.\s|\s*(&gt;|>)\s)/.test(lines[i]) &&
                    !/^\s*\|.+\|\s*$/.test(lines[i])
                ) {
                    paragraph.push(lines[i])
                    i++
                }
                blocks.push(`<p class="md-p">${paragraph.join('<br>')}</p>`)
            }

            let out = blocks.filter((b) => b !== '').join('')

            // 3. Inline formatting inside already-built HTML (bold, italic, code, links)
            out = out
                .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
                .replace(/(^|[\s>])\*([^*\n]+?)\*(?=[\s<.,!?])/g, '$1<em>$2</em>')
                .replace(/`([^`]+?)`/g, '<code>$1</code>')
                // Markdown links — same-origin only (everything else stays plain text)
                .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (full, label, url) => {
                    if (url.indexOf(window.location.origin) === 0) {
                        return `<a class="md-link" href="${url}">${label}</a>`
                    }
                    return label
                })

            return out
        },
        scrollToBottom() {
            this.$nextTick(() => {
                if (this.$refs.chatBody) {
                    this.$refs.chatBody.scrollTop = this.$refs.chatBody.scrollHeight
                }
            })
        },
    },
    mounted() {
        const metaToken = document.querySelector('meta[name="csrf-token"]')
        if (metaToken) {
            this.csrfToken = metaToken.getAttribute('content')
        }
        EventBus.$on('open-chatbot', this.openChat)
        document.addEventListener('keydown', this.onEscape)

        // Première visite : le panneau reste fermé, on désigne le bouton 1,5 s après le chargement.
        // Une seule fois par établissement (drapeau en base, cf. markCoachSeen).
        if (!(window.AiAssistant && window.AiAssistant.coach_seen)) {
            this.coachTimer = setTimeout(() => {
                const btn = this.$el && this.$el.querySelector('.btnAI')
                const btnVisible = btn && btn.getBoundingClientRect().width > 0
                if (coachClaimed || !btnVisible || this.chatVisible) return
                coachClaimed = true
                this.coachVisible = true
            }, 1500)
        }
    },
    beforeDestroy() {
        EventBus.$off('open-chatbot', this.openChat)
        document.removeEventListener('keydown', this.onEscape)
        if (this.coachTimer) clearTimeout(this.coachTimer)
    },
}
</script>

<style scoped>
.btnAI {
    background: linear-gradient(to right, #64a1ee 0%, #bc51e6 50%, #d84895 100%);
    padding: 8px 14px;
    color: white;
    border-radius: 12px;
    font-size: 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.3s;
    white-space: nowrap;
    min-width: max-content;
}
@media (max-width: 348px) {
    .btnAI {
        font-size: 11px;
        padding: 6px 10px;
        border-radius: 8px;
    }
}
.btnAI:hover {
    opacity: 0.85;
}

/* ════════════════════════════════════════════════════════════════════════
   Fenêtre de chat — design "AI-native premium"
   Tokens : radius 20/16, easing cubic-bezier(0.16,1,0.3,1), gap 16px,
   accent #8a38f5 → #d84895, surfaces glass (blur 16px, hairline borders)
   ════════════════════════════════════════════════════════════════════════ */
.chat-popup {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 400px;
    height: 640px;
    max-height: calc(100vh - 40px);
    /* Surface pleine (maquette) plutôt qu'un verre dégradé : le contenu porte la couleur,
       pas le fond. Le dark mode garde sa propre surface. */
    background: #fbfaff;
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(10, 2, 30, 0.5);
    color: #20123f;
    font-family: Poppins, 'Inter', sans-serif;
    z-index: 10000;
    border: 1px solid rgba(178, 146, 255, 0.18);
    /* Se replie vers le bouton qui l'a ouvert (en haut à droite), au lieu de disparaître. */
    transform-origin: 100% 0;
    transition:
        transform 0.2s cubic-bezier(0.4, 0, 0.7, 1),
        opacity 0.18s ease;
}
.chat-popup--closed {
    transform: translateY(-10px) scale(0.94);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
}
@media (prefers-reduced-motion: reduce) {
    .chat-popup {
        transition: none;
    }
}
@media (max-width: 768px) {
    .chat-popup {
        width: calc(100vw - 24px);
        right: 12px;
        bottom: 12px;
        height: 70vh;
    }
}
.dark .chat-popup {
    background: #171233;
    border-color: rgba(255, 255, 255, 0.08);
    color: #ededef;
    box-shadow:
        0 24px 60px -12px rgba(0, 0, 0, 0.6),
        0 0 0 1px rgba(255, 255, 255, 0.04);
}

/* ── Header : identité + statut ───────────────────────────────────────── */
.chat-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(27, 21, 61, 0.07);
    flex-shrink: 0;
}
.dark .chat-header {
    border-bottom-color: rgba(255, 255, 255, 0.07);
}
.header-identity {
    display: flex;
    align-items: center;
    gap: 11px;
    min-width: 0;
}
.header-avatar {
    position: relative;
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(138, 56, 245, 0.16), rgba(216, 72, 149, 0.12));
    border: 1px solid rgba(138, 56, 245, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.header-avatar img {
    height: 18px;
}
.header-status-dot {
    position: absolute;
    right: -2px;
    bottom: -2px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #10b981;
    border: 2px solid #fff;
}
.dark .header-status-dot {
    border-color: #140f30;
}
.header-status-dot--off {
    background: #f0a13a;
}
.header-text {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
    min-width: 0;
}
.header-title {
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.header-subtitle {
    font-size: 11px;
    opacity: 0.55;
}
.close-btn {
    background: none;
    border: none;
    cursor: pointer;
    border-radius: 10px;
    padding: 6px;
    display: flex;
    transition: background 0.15s ease;
}
.close-btn:hover {
    background: rgba(138, 56, 245, 0.1);
}
.close-btn:focus-visible {
    outline: 2px solid #8a38f5;
    outline-offset: 2px;
}

/* ── Corps : messages ─────────────────────────────────────────────────── */
.chat-body {
    flex: 1;
    /* min-height:0 obligatoire : sans ça, un long contenu empêche le flex-child
       de rétrécir et pousse le footer (input) hors de la fenêtre */
    min-height: 0;
    overflow-y: auto;
    padding: 18px 16px;
    scroll-behavior: smooth;
}
.chat-body::-webkit-scrollbar {
    width: 5px;
}
.chat-body::-webkit-scrollbar-thumb {
    background: rgba(138, 56, 245, 0.25);
    border-radius: 999px;
}
.chat-body::-webkit-scrollbar-thumb:hover {
    background: rgba(138, 56, 245, 0.45);
}

/* État vide : salutation en haut, actions ancrées en bas près du champ de saisie. */
.chat-intro {
    display: flex;
    flex-direction: column;
    min-height: 100%;
}
.greet {
    margin: 4px 0 16px;
}
.greet h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 4px;
}
.greet p {
    margin: 0;
    font-size: 13.5px;
    opacity: 0.65;
    line-height: 1.5;
}

.message {
    max-width: 82%;
    margin-bottom: 14px;
    padding: 11px 15px;
    line-height: 1.55;
    word-wrap: break-word;
    font-size: 13px;
    animation: msg-in 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes msg-in {
    from {
        opacity: 0;
        transform: translateY(6px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.user-message {
    font-family: Poppins, sans-serif;
    background: linear-gradient(135deg, rgba(138, 56, 245, 0.14), rgba(216, 72, 149, 0.1));
    border: 1px solid rgba(138, 56, 245, 0.18);
    border-radius: 16px 16px 4px 16px;
    align-self: flex-end;
    margin-left: auto;
}
.dark .user-message {
    background: linear-gradient(135deg, rgba(138, 56, 245, 0.3), rgba(216, 72, 149, 0.2));
    border-color: rgba(255, 255, 255, 0.1);
}
.ai-message {
    font-family: Poppins, sans-serif;
    max-width: 95%;
    align-self: flex-start;
    margin-right: auto;
    background: rgba(27, 21, 61, 0.045);
    border: 1px solid rgba(27, 21, 61, 0.06);
    border-radius: 16px 16px 16px 4px;
}
.dark .ai-message {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.07);
}
/* ── Actions proposées par l'IA (navigation / confirmation) ───────────── */
.message-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 6px 0 12px;
}
.action-btn {
    font-family: Poppins, sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 999px;
    cursor: pointer;
    transition:
        background 0.15s ease,
        box-shadow 0.15s ease,
        transform 0.1s ease;
}
.action-btn:focus-visible {
    outline: 2px solid #8a38f5;
    outline-offset: 2px;
}
.action-btn:active {
    transform: scale(0.97);
}
.action-btn:disabled {
    cursor: default;
    opacity: 0.55;
    transform: none;
}
.action-btn__icon {
    font-size: 12px;
}
/* Navigation = outline (action réversible) */
.action-btn--navigate {
    border: 1px solid #8a38f5;
    background: transparent;
    color: #7d2dbd;
}
.action-btn--navigate:hover:not(:disabled) {
    background: rgba(138, 56, 245, 0.12);
}
.dark .action-btn--navigate {
    border-color: rgba(255, 255, 255, 0.45);
    color: #fff;
}
.dark .action-btn--navigate:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
}
/* Confirmation = rempli (action engageante, écrit en base) */
.action-btn--confirm {
    border: none;
    background: linear-gradient(to right, #8a38f5, #d84895);
    color: #fff;
    box-shadow: 0 2px 8px rgba(138, 56, 245, 0.35);
}
.action-btn--confirm:hover:not(:disabled) {
    box-shadow: 0 4px 14px rgba(138, 56, 245, 0.5);
}
.action-btn--done {
    background: rgba(46, 160, 67, 0.15);
    border: 1px solid #2ea043;
    color: #2ea043;
    box-shadow: none;
}

/* ── Saisie de la clé Gemini (intégration manquante) ──────────────────── */
.key-card {
    margin: 0 0 14px;
    max-width: 95%;
}
.key-card__row {
    display: flex;
    flex-direction: column;
    gap: 7px;
}
/* Monospace : on relit une clé collée caractère par caractère, pas en texte courant. */
.key-card__input {
    width: 100%;
    min-width: 0;
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 11.5px;
    padding: 10px 9px;
    border-radius: 10px;
    border: 1px solid #ddd2f6;
    background: #fff;
    outline: none;
}
.key-card__input:focus {
    border-color: #8a38f5;
    box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.15);
}
.dark .key-card__input {
    border-color: rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.07);
    color: #ededef;
}
.key-card__btn {
    font-family: Poppins, sans-serif;
    font-size: 13px;
    font-weight: 500;
    padding: 9px 13px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(94deg, #a855f7, #ec4899);
    color: #fff;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(138, 56, 245, 0.35);
}
.key-card__btn:disabled {
    opacity: 0.5;
    cursor: default;
    box-shadow: none;
}
.key-card__btn:focus-visible,
.key-card__input:focus-visible {
    outline: 2px solid #8a38f5;
    outline-offset: 2px;
}
.key-card__hint {
    margin: 0 0 8px;
    font-size: 12.5px;
    line-height: 1.5;
    opacity: 0.8;
}
.key-card__hint a {
    color: #6d28d9;
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
    white-space: nowrap;
}
.dark .key-card__hint a {
    color: #cdb4ff;
}
.key-card__err {
    font-size: 12px;
    color: #c0392b;
    margin: 7px 0 0;
}
.key-card__done {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 12.6px;
    color: #0f8f66;
}
.dark .key-card__done {
    color: #6ee7bd;
}

/* ── Pastille « Louise a un message » + halo première visite ──────────── */
.btnAI {
    position: relative;
}
.btnAI__badge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 19px;
    height: 19px;
    padding: 0 5px;
    border-radius: 10px;
    background: #ef4444;
    color: #fff;
    font-size: 11.5px;
    font-weight: 600;
    line-height: 19px;
    text-align: center;
    box-shadow: 0 0 0 2px rgba(20, 8, 45, 0.6);
    animation: badge-drop 0.42s cubic-bezier(0.2, 1.5, 0.4, 1);
}
@keyframes badge-drop {
    from {
        opacity: 0;
        transform: scale(0.3);
    }
}
.btnAI--spot {
    z-index: 10002;
}
.btnAI__halo {
    position: absolute;
    inset: -5px;
    border-radius: 16px;
    border: 2px solid #c4a6ff;
    pointer-events: none;
    animation: halo 1.9s ease-out infinite;
}
@keyframes halo {
    0% {
        transform: scale(0.96);
        opacity: 0.9;
    }
    70% {
        transform: scale(1.13);
        opacity: 0;
    }
    100% {
        opacity: 0;
    }
}

/* ── Bulle de première visite ─────────────────────────────────────────── */
.coach-veil {
    position: fixed;
    inset: 0;
    background: rgba(11, 3, 28, 0.62);
    z-index: 10001;
    animation: coach-fade 0.3s;
}
@keyframes coach-fade {
    from {
        opacity: 0;
    }
}
.coach {
    position: absolute;
    top: 53px;
    right: 38%;
    width: 360px;
    max-width: calc(100vw - 24px);
    background: #fbfaff;
    color: #20123f;
    border-radius: 18px;
    padding: 20px;
    z-index: 10003;
    box-shadow: 0 26px 70px rgba(8, 2, 26, 0.6);
    font-family: Poppins, 'Inter', sans-serif;
    animation: coach-pop 0.38s cubic-bezier(0.2, 1.2, 0.4, 1);
}
.coach::before {
    content: '';
    position: absolute;
    top: -10px;
    right: 28px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #fbfaff;
}
.dark .coach::before {
    border-bottom-color: #171233;
}
@keyframes coach-pop {
    from {
        opacity: 0;
        transform: translateY(-12px) scale(0.97);
    }
}
.coach__spark {
    width: 36px;
    height: 36px;
    border-radius: 11px;
    background: #efe7ff;
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 13px;
}
.coach h3 {
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.3;
}
.coach p {
    margin: 0 0 14px;
    font-size: 13.4px;
    color: #615089;
    line-height: 1.55;
}
.coach ul {
    margin: 0 0 17px;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.coach li {
    display: flex;
    gap: 9px;
    font-size: 13px;
    align-items: flex-start;
}
.coach li svg {
    color: #7c3aed;
    flex: none;
    margin-top: 1px;
}
.coach__actions {
    display: flex;
    gap: 8px;
    align-items: center;
}
.coach__go {
    font-family: Poppins, sans-serif;
    font-size: 13px;
    padding: 9px 13px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(94deg, #a855f7, #ec4899);
    color: #fff;
    cursor: pointer;
}
.coach__later {
    margin-left: auto;
    font-family: Poppins, sans-serif;
    font-size: 12.5px;
    color: #8d80ab;
    background: none;
    border: 0;
    cursor: pointer;
    padding: 8px;
}
.coach__go:focus-visible,
.coach__later:focus-visible {
    outline: 2px solid #8a38f5;
    outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
    .coach,
    .coach-veil,
    .btnAI__badge {
        animation: none;
    }
    .btnAI__halo {
        animation: none;
        opacity: 0.9;
    }
}

.header-subtitle--off::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f0a13a;
    margin-right: 5px;
    vertical-align: 1px;
}

/* ── Actions rapides (état vide) ──────────────────────────────────────────
   Sept lignes courtes collées au champ de saisie (margin-top:auto) : bordure et
   fond portent l'affordance de clic, le chevron n'apparaît qu'au survol pour ne
   pas bruiter sept lignes d'un coup. */
.sugg-zone {
    margin-top: auto;
}
.sugg {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
/* Questions par défaut : sous les actions, en retrait typographique pour rester secondaires. */
.sugg-questions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
}
.sugg-question {
    font-family: Poppins, sans-serif;
    font-size: 12px;
    line-height: 1;
    padding: 8px 12px;
    border-radius: 999px;
    border: 1px solid #e2d8f8;
    background: transparent;
    color: inherit;
    opacity: 0.75;
    cursor: pointer;
    transition:
        opacity 0.14s ease,
        border-color 0.14s ease,
        background 0.14s ease;
}
.sugg-question:hover {
    opacity: 1;
    border-color: #b58cf5;
    background: rgba(138, 56, 245, 0.06);
}
.sugg-question:focus-visible {
    outline: 2px solid #8a38f5;
    outline-offset: 2px;
}
.dark .sugg-question {
    border-color: rgba(255, 255, 255, 0.12);
}
.dark .sugg-question:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(197, 164, 255, 0.5);
}
.sugg-card {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 42px;
    text-align: left;
    font-family: Poppins, sans-serif;
    cursor: pointer;
    background: #fff;
    border: 1px solid #e2d8f8;
    border-radius: 11px;
    padding: 0 11px;
    color: inherit;
    box-shadow: 0 1px 2px rgba(60, 20, 120, 0.05);
    opacity: 0;
    transform: translateY(8px);
    animation: sugg-rise 0.34s forwards;
    transition:
        transform 0.14s ease,
        border-color 0.14s ease,
        box-shadow 0.14s ease;
}
@keyframes sugg-rise {
    to {
        opacity: 1;
        transform: none;
    }
}
.sugg-card:hover {
    border-color: #b58cf5;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(96, 40, 190, 0.13);
}
.sugg-card:focus-visible {
    outline: 2px solid #8a38f5;
    outline-offset: 2px;
}
.dark .sugg-card {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
}
.dark .sugg-card:hover {
    border-color: rgba(197, 164, 255, 0.6);
    background: rgba(255, 255, 255, 0.08);
}
.sugg-ico {
    width: 26px;
    height: 26px;
    flex: none;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}
/* L'icône encode la famille : violet = consulter, bleu = analyser, vert = créer. */
.sugg-ico.i-vio {
    background: #f0e8ff;
    color: #7c3aed;
}
.sugg-ico.i-blu {
    background: #e6effc;
    color: #2563a8;
}
.sugg-ico.i-gre {
    background: #e2f7ee;
    color: #0f8f66;
}
.sugg-card b {
    font-size: 13.5px;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.sugg-card .go {
    margin-left: auto;
    display: flex;
    flex: none;
    color: #cfc3ea;
    opacity: 0;
    transition: opacity 0.14s ease;
}
.sugg-card:hover .go {
    color: #7c3aed;
    opacity: 1;
}
.dark .sugg-card:hover .go {
    color: #cdb4ff;
}
@media (prefers-reduced-motion: reduce) {
    .sugg-card {
        opacity: 1;
        transform: none;
        animation: none;
    }
}

/* ── Indicateur de frappe ─────────────────────────────────────────────── */
.typing-indicator {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 12px 16px;
    width: fit-content;
}
.typing-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #8a38f5;
    animation: typing-bounce 1.2s infinite ease-in-out;
}
.dark .typing-dot {
    background: rgba(255, 255, 255, 0.75);
}
.typing-dot:nth-child(2) {
    animation-delay: 0.15s;
}
.typing-dot:nth-child(3) {
    animation-delay: 0.3s;
}
@keyframes typing-bounce {
    0%,
    60%,
    100% {
        transform: translateY(0);
        opacity: 0.45;
    }
    30% {
        transform: translateY(-4px);
        opacity: 1;
    }
}
@media (prefers-reduced-motion: reduce) {
    .typing-dot {
        animation: none;
        opacity: 0.7;
    }
    .action-btn,
    .action-btn:active,
    .suggestion-chip,
    .send-btn {
        transition: none;
        transform: none;
    }
    .chat-popup,
    .message {
        animation: none;
    }
}

/* ── Liens markdown dans les réponses ─────────────────────────────────── */
.ai-message >>> .md-link {
    color: #7d2dbd;
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
}
.dark .ai-message >>> .md-link {
    color: #cdb4ff;
}

.send-btn {
    border: none;
    background: linear-gradient(135deg, #8a38f5, #d84895);
    color: #fff !important;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        box-shadow 0.18s ease,
        transform 0.12s ease;
    box-shadow: 0 3px 10px rgba(138, 56, 245, 0.35);
}
.send-btn:hover:not(:disabled) {
    box-shadow: 0 6px 18px rgba(138, 56, 245, 0.5);
}
.send-btn:active:not(:disabled) {
    transform: scale(0.95);
}
.send-btn:focus-visible {
    outline: 2px solid #8a38f5;
    outline-offset: 2px;
}
.send-btn:disabled {
    opacity: 0.4;
    cursor: default;
    box-shadow: none;
}
.border-footer {
    border: 1px solid rgba(138, 56, 245, 0.3);
    background: rgba(255, 255, 255, 0.65);
    border-radius: 14px;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 4px 6px 4px 0;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}
.border-footer:focus-within {
    border-color: #8a38f5;
    box-shadow: 0 0 0 3px rgba(138, 56, 245, 0.14);
}
.dark .border-footer {
    border-color: rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.07);
}
.dark .border-footer:focus-within {
    border-color: rgba(138, 56, 245, 0.7);
}
.chat-footer {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    flex-shrink: 0;
}
.chat-footer input {
    flex: 1;
    border: none;
    outline: none;
    width: calc(100% - 45px);
}
.chat-footer input {
    background: transparent;
}
.chat-footer input::placeholder {
    color: rgba(27, 21, 61, 0.45);
}
.dark .chat-footer input::placeholder {
    color: rgba(255, 255, 255, 0.5);
}
.chat-footer button {
    width: 40px;
    height: 40px;
    font-size: 16px;
    cursor: pointer;
    flex-shrink: 0;
}

/* ===== Markdown rendering inside AI messages ===== */
.ai-message >>> .md-p {
    margin: 0 0 8px 0;
}
.ai-message >>> .md-p:last-child {
    margin-bottom: 0;
}
.ai-message >>> .md-h {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    margin: 10px 0 6px 0;
    color: #0f172a;
    line-height: 1.3;
}
.dark .ai-message >>> .md-h {
    color: #fff;
}
.ai-message >>> h3.md-h {
    font-size: 15px;
}
.ai-message >>> h4.md-h {
    font-size: clamp(12px, 0.729545vw, 16px);
}
.ai-message >>> h5.md-h {
    font-size: 13px;
}
.ai-message >>> .md-ul,
.ai-message >>> .md-ol {
    margin: 6px 0 8px 0;
    padding-left: 20px;
}
.ai-message >>> .md-ul li,
.ai-message >>> .md-ol li {
    margin: 3px 0;
}
.ai-message >>> strong {
    color: #0f172a;
    font-weight: 600;
}
.dark .ai-message >>> strong {
    color: #fff;
}
.ai-message >>> code {
    background: rgba(15, 23, 42, 0.08);
    padding: 1px 6px;
    border-radius: 4px;
    font-family: 'Menlo', 'Monaco', monospace;
    font-size: 12px;
}
.dark .ai-message >>> code {
    background: rgba(255, 255, 255, 0.12);
    color: #f1f5f9;
}
.ai-message >>> .md-table-wrap {
    overflow-x: auto;
    margin: 8px 0;
    border-radius: 8px;
}
.ai-message >>> .md-quote {
    margin: 8px 0;
    padding: 6px 12px;
    border-left: 3px solid #8a38f5;
    background: rgba(138, 56, 245, 0.06);
    border-radius: 0 8px 8px 0;
}
.dark .ai-message >>> .md-quote {
    background: rgba(255, 255, 255, 0.05);
}
.ai-message >>> .md-table {
    border-collapse: collapse;
    min-width: 100%;
    margin: 0;
    background: rgba(255, 255, 255, 0.75);
    border-radius: 8px;
    overflow: hidden;
    font-size: 12px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}
.dark .ai-message >>> .md-table {
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.ai-message >>> .md-table thead {
    background: linear-gradient(to right, rgba(138, 56, 245, 0.15), rgba(216, 72, 149, 0.12));
}
.ai-message >>> .md-table th {
    padding: 7px 9px;
    text-align: left;
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
    color: #0f172a;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
}
.dark .ai-message >>> .md-table th {
    color: #f1f5f9;
}
.ai-message >>> .md-table td {
    padding: 6px 9px;
    color: #1e293b;
    border-top: 1px solid rgba(15, 23, 42, 0.06);
    vertical-align: top;
}
.dark .ai-message >>> .md-table td {
    color: #e2e8f0;
    border-top-color: rgba(255, 255, 255, 0.06);
}
.ai-message >>> .md-table tbody tr:hover {
    background: rgba(138, 56, 245, 0.05);
}

/* Tableau large (> 3 colonnes) : empilé en « libellé : valeur », une carte par ligne.
   Un tableau à 5 colonnes dans une popup de 400px est illisible même avec un scroll latéral. */
.ai-message >>> .md-table--stacked thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
}
.ai-message >>> .md-table--stacked tbody tr {
    display: block;
    padding: 4px 0;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
}
.dark .ai-message >>> .md-table--stacked tbody tr {
    border-top-color: rgba(255, 255, 255, 0.08);
}
.ai-message >>> .md-table--stacked tbody tr:first-child {
    border-top: none;
}
.ai-message >>> .md-table--stacked td {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    border-top: none;
    padding: 3px 10px;
}
.ai-message >>> .md-table--stacked td::before {
    content: attr(data-label);
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    opacity: 0.6;
    flex-shrink: 0;
}
/* Première colonne = en-tête de la carte (la date, le nom…) */
.ai-message >>> .md-table--stacked td:first-child {
    font-weight: 600;
    padding-top: 7px;
}
.ai-message >>> .md-table--stacked td:first-child::before {
    display: none;
}
</style>
